import { useState } from "react";
import { motion } from "framer-motion";
import { Gauge, Cog, ArrowLeft, ArrowRight, Check } from "lucide-react";

const ZIELE = [
    "Mehr Leistung",
    "Mehr Drehmoment",
    "Weniger Verbrauch",
    "DSG Optimierung",
    "Pops & Bangs",
    "VMAX Aufhebung",
];

export const CheckStep = ({ onComplete, onBack }) => {
    const [km, setKm] = useState("");
    const [getriebe, setGetriebe] = useState("");
    const [ziele, setZiele] = useState([]);
    const [sonstiges, setSonstiges] = useState("");

    const toggleZiel = (z) =>
        setZiele((arr) => (arr.includes(z) ? arr.filter((x) => x !== z) : [...arr, z]));

    const canContinue = km && getriebe && ziele.length > 0;

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="mx-auto max-w-2xl"
        >
            <div className="text-center">
                <h2 className="font-heading text-2xl font-bold text-white sm:text-3xl">
                    KI Performance Check
                </h2>
                <p className="mt-2 text-sm text-[#A1A1AA]">
                    Nur noch ein paar Angaben für eine präzise Empfehlung.
                </p>
            </div>

            <div className="mt-8 space-y-6">
                {/* Kilometerstand */}
                <div className="rounded-2xl border border-border bg-surface p-5 sm:p-6">
                    <label className="flex items-center gap-2 text-sm font-medium text-white">
                        <Gauge className="h-4 w-4 text-primary" /> Aktueller Kilometerstand
                    </label>
                    <input
                        data-testid="check-km"
                        type="number"
                        value={km}
                        onChange={(e) => setKm(e.target.value)}
                        placeholder="z.B. 85000"
                        className="mt-3 w-full rounded-md border border-border bg-[#0A0A0A] px-4 py-3 text-sm text-white placeholder:text-[#52525B] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                    />
                </div>

                {/* Getriebe */}
                <div className="rounded-2xl border border-border bg-surface p-5 sm:p-6">
                    <label className="flex items-center gap-2 text-sm font-medium text-white">
                        <Cog className="h-4 w-4 text-primary" /> Getriebe
                    </label>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                        {["Automatik", "Schalter"].map((g) => (
                            <button
                                key={g}
                                data-testid={`getriebe-${g.toLowerCase()}`}
                                onClick={() => setGetriebe(g)}
                                className={`rounded-md border px-4 py-3.5 text-sm font-medium transition-colors ${
                                    getriebe === g
                                        ? "border-primary bg-primary/15 text-white"
                                        : "border-border bg-[#0A0A0A] text-[#A1A1AA] hover:border-primary/40"
                                }`}
                            >
                                {g}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Ziele */}
                <div className="rounded-2xl border border-border bg-surface p-5 sm:p-6">
                    <label className="text-sm font-medium text-white">
                        Deine Ziele <span className="text-[#52525B]">(Mehrfachauswahl)</span>
                    </label>
                    <div className="mt-3 flex flex-wrap gap-2.5">
                        {ZIELE.map((z) => {
                            const active = ziele.includes(z);
                            return (
                                <button
                                    key={z}
                                    data-testid={`ziel-${z.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
                                    onClick={() => toggleZiel(z)}
                                    className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors ${
                                        active
                                            ? "border-primary bg-primary text-white"
                                            : "border-border bg-[#0A0A0A] text-[#A1A1AA] hover:border-primary/40"
                                    }`}
                                >
                                    {active && <Check className="h-3.5 w-3.5" />}
                                    {z}
                                </button>
                            );
                        })}
                    </div>

                    <label className="mt-5 block text-sm font-medium text-white">Sonstige Wünsche</label>
                    <textarea
                        data-testid="check-sonstiges"
                        value={sonstiges}
                        onChange={(e) => setSonstiges(e.target.value)}
                        rows={3}
                        placeholder="z.B. spezielle Anforderungen, geplante Hardware..."
                        className="mt-2 w-full resize-none rounded-md border border-border bg-[#0A0A0A] px-4 py-3 text-sm text-white placeholder:text-[#52525B] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                    />
                </div>
            </div>

            <div className="mt-6 flex gap-3">
                <button
                    data-testid="check-back-btn"
                    onClick={onBack}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface-elevated px-5 py-3.5 text-sm font-medium text-white hover:border-primary/50 transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" /> Zurück
                </button>
                <button
                    data-testid="check-analyse-btn"
                    disabled={!canContinue}
                    onClick={() => onComplete({ kilometerstand: km, getriebe, ziele, sonstiges })}
                    className="group inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-primary px-6 py-3.5 text-base font-bold text-white hover:bg-[#FF3B30] transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                >
                    KI-Analyse erstellen
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
            </div>
        </motion.div>
    );
};

export default CheckStep;
